import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, StarIcon, ShieldCheckIcon, ClockIcon, LockIcon } from 'lucide-react';
interface PricingSelectorProps {
  variant?: 'full' | 'compact';
  idPrefix?: string;
}
const tiers = {
  standard: {
    id: 'standard',
    name: 'Mujer Expansiva',
    tagline: 'La guía completa para empezar tu transformación',
    price: '19.990',
    oldPrice: '29.990',
    url: 'https://guadaquenan.online/cart/52640586432801:1',
    image: 'https://cdn.magicpatterns.com/uploads/oP8QhEYNcRD5PtVNgjorem/Gemini_Generated_Image_obayvtobayvtobay.png',
    features: ['Ebook completo · 4 Partes + 180 páginas', '5 Bonos estratégicos incluidos', 'Biblioteca digital + 25 libros', 'Acceso inmediato y de por vida']
  },
  premium: {
    id: 'premium',
    name: 'Mujer Expansiva Premium',
    tagline: 'Para la mujer que no solo quiere el mapa... sino llegar',
    price: '34.999',
    oldPrice: '54.999',
    url: 'https://guadaquenan.online/cart/52775470465313:1',
    image: 'https://cdn.magicpatterns.com/uploads/vRYNhDpYSriiQPP2H96Mj6/MOCKUP_2.jpg',
    features: ['Todo lo del plan estándar +', '4 Plantillas y workbooks descargables', 'Sesión grupal en vivo mensual', '30 días de soporte por voz vía Instagram', 'Plan de acción personalizado']
  }
};
export function PricingSelector({
  variant = 'full',
  idPrefix = ''
}: PricingSelectorProps) {
  const [selected, setSelected] = useState<'standard' | 'premium'>('premium');
  const activeTier = tiers[selected];
  return <div className="max-w-3xl mx-auto" data-id="element-220">
      {/* Tier Selector — horizontal rows, stacked vertically */}
      <div className="flex flex-col gap-3 md:gap-4 mb-6" data-id="element-221">
        {(['premium', 'standard'] as const).map(key => {
        const tier = tiers[key];
        const isSelected = selected === key;
        const isPremium = key === 'premium';
        return <button key={key} type="button" onClick={() => setSelected(key)} className={`relative w-full text-left rounded-md border-2 transition-all duration-300 overflow-hidden ${isSelected ? isPremium ? 'border-gold bg-plum text-cream shadow-xl shadow-gold/20' : 'border-plum bg-cream text-plum shadow-lg' : 'border-plum/15 bg-cream/60 text-plum hover:border-gold/40 hover:bg-cream'}`} data-id="element-222">
              {/* Más Elegido strip — premium */}
              {isPremium && <div className={`absolute top-0 right-0 px-3 py-1 text-[9px] md:text-[10px] font-bold tracking-[0.2em] uppercase flex items-center gap-1 rounded-bl-md transition-all ${isSelected ? 'bg-gold text-white' : 'bg-plum/80 text-cream'}`} data-id="element-223">
                  <StarIcon size={9} fill="currentColor" data-id="element-224" /> Más Elegido
                </div>}

              <div className="flex items-stretch gap-3 md:gap-5 p-3 md:p-5" data-id="element-225">
                {/* Mockup thumb */}
                <div className="flex-shrink-0" data-id="element-226">
                  <div className={`relative w-20 h-20 md:w-28 md:h-28 rounded overflow-hidden ${isPremium && isSelected ? 'ring-2 ring-gold/60' : 'ring-1 ring-plum/10'}`} data-id="element-227">
                    <img src={tier.image} alt={tier.name} width="112" height="112" loading="lazy" decoding="async" className="w-full h-full object-cover" data-id="element-228" />
                  </div>
                </div>

                {/* Center content */}
                <div className="flex-1 min-w-0 flex flex-col justify-center pr-1" data-id="element-229">
                  <h3 className={`font-serif text-base md:text-xl leading-tight mb-1 pr-16 md:pr-20 ${isPremium && isSelected ? 'text-gold' : ''}`} data-id="element-230">
                    {tier.name}
                  </h3>
                  <p className={`text-[11px] md:text-sm leading-snug mb-2 opacity-70 line-clamp-2 ${isPremium && isSelected ? 'text-cream/70' : 'text-plum/60'}`} data-id="element-231">
                    {tier.tagline}
                  </p>
                  <div className="flex items-baseline gap-2 flex-wrap" data-id="element-232">
                    <span className="font-serif text-xl md:text-2xl font-bold" data-id="element-233">
                      ${tier.price}
                    </span>
                    <span className="text-[10px] md:text-xs line-through opacity-50" data-id="element-234">
                      ${tier.oldPrice}
                    </span>
                  </div>
                </div>

                {/* Right radio */}
                <div className="flex items-center flex-shrink-0" data-id="element-235">
                  <div className={`w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? isPremium ? 'border-gold bg-gold' : 'border-plum bg-plum' : 'border-current opacity-30'}`} data-id="element-236">
                    {isSelected && <div className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-white" data-id="element-237" />}
                  </div>
                </div>
              </div>
            </button>;
      })}
      </div>

      {/* Selected tier details (only in full variant) */}
      {variant === 'full' && <AnimatePresence mode="wait" data-id="element-238">
          <motion.div key={selected} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.3
      }} className={`p-5 md:p-7 rounded-md mb-5 md:mb-6 ${selected === 'premium' ? 'bg-plum text-cream border border-gold/30' : 'bg-cream text-plum border border-plum/10'}`} data-id="element-239">
            <ul className="space-y-2 md:space-y-2.5" data-id="element-240">
              {activeTier.features.map((feature, i) => <li key={i} className="flex items-start gap-2.5 md:gap-3" data-id="element-241">
                  <CheckIcon className={`flex-shrink-0 mt-0.5 md:mt-1 ${selected === 'premium' ? 'text-gold' : 'text-plum/50'}`} size={16} data-id="element-242" />
                  <span className={`text-sm md:text-base leading-snug ${selected === 'premium' ? 'text-cream/90' : 'text-plum/85'}`} data-id="element-243">
                    {feature}
                  </span>
                </li>)}
            </ul>
          </motion.div>
        </AnimatePresence>}

      {/* Single shared CTA button */}
      <motion.a key={selected + '-cta'} href={activeTier.url} target="_blank" rel="noopener noreferrer" initial={{
      opacity: 0.8
    }} animate={{
      opacity: 1
    }} whileHover={{
      scale: 1.01
    }} whileTap={{
      scale: 0.99
    }} className={`block w-full text-center px-6 py-4 md:py-5 text-sm md:text-base font-bold tracking-wide uppercase transition-all duration-300 rounded-sm shadow-xl ${selected === 'premium' ? 'bg-gold text-white hover:bg-gold-light shadow-gold/30' : 'bg-plum text-cream hover:bg-plum-light shadow-plum/20'}`} data-id="element-244">
        💜 Quiero {activeTier.name} — ${activeTier.price}
      </motion.a>

      {/* Trust line */}
      <div className="flex flex-wrap justify-center items-center gap-x-3 md:gap-x-5 gap-y-2 mt-4 md:mt-5 text-[10px] md:text-xs font-medium uppercase tracking-wider opacity-60" data-id="element-245">
        <span className="flex items-center gap-1" data-id="element-246">
          <ShieldCheckIcon size={12} data-id="element-247" /> Garantía 7 días
        </span>
        <span className="flex items-center gap-1" data-id="element-248">
          <ClockIcon size={12} data-id="element-249" /> Acceso en 5 min
        </span>
        <span className="flex items-center gap-1" data-id="element-250">
          <LockIcon size={12} data-id="element-251" /> Pago seguro
        </span>
      </div>
    </div>;
}